import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

type MainTab =
  | 'my-cases'
  | 'raised-concerns'
  | 'assessment-results'
  | 'referrals';
type CaseView = 'card' | 'table';

interface CaseItem {
  id?: number;

  case_number: string;

  first_name?: string;
  middle_name?: string;
  last_name?: string;
  nickname?: string;

  age?: number | string;
  gender?: string;

  incident_type?: string;
  incident_description?: string;
  incident_location?: string;
  barangay?: string;
  location?: string;

  priority_level?: string;
  prior_incidents?: string;

  case_status?: string;
  progress_note?: string;

  created_at?: string;
  updated_at?: string;

  uid?: string;

  // Referral fields
  assigned_to?: string;
  assigned_name?: string;
  social_worker_name?: string;

  // Assessment / risk fields
  risk_level?: string;
  risk_score?: number | string;
  score?: number | string;

  mark_as_priority?: number | boolean;

  [key: string]: any;
}

interface ReportItem {
  id: number;
  report_uid: string;
  victim_uid: string;

  child_first_name?: string;
  child_middle_name?: string;
  child_last_name?: string;
  child_nickname?: string;
  age?: number | string;
  gender?: string;

  incident_type: string;
  urgency: string;
  description: string;
  location: string;

  child_info?: string;

  is_anonymous: string;
  reporter_name: string;
  reporter_contact: string;

  photo_evidence: string;
  status: string;
  created_at: string;

  victim_name?: string;

  [key: string]: any;
}

const normalizeStatus = (status?: string) => {
  return String(status || '').trim().toLowerCase();
};

const formatStatus = (status?: string) => {
  if (!status) return 'No Status';

  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDate = (value?: string) => {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getChildName = (c: CaseItem) => {
  const name = [
    c.first_name,
    c.middle_name,
    c.last_name,
  ]
    .filter(Boolean)
    .join(' ');

  return name || 'Unnamed Child';
};

const getReportChildName = (report: ReportItem) => {
  if (report.victim_name) {
    return report.victim_name;
  }

  const name = [
    report.child_first_name,
    report.child_middle_name,
    report.child_last_name,
  ]
    .filter(Boolean)
    .join(' ');

  return name || 'Child / Victim';
};

const riskColor = (risk: string) => {
  switch (risk) {
    case 'critical':
      return 'bg-red-100 text-red-700 border-red-200';

    case 'high':
      return 'bg-orange-100 text-orange-700 border-orange-200';

    case 'moderate':
      return 'bg-amber-100 text-amber-700 border-amber-200';

    default:
      return 'bg-green-100 text-green-700 border-green-200';
  }
};

const getRisk = (c: CaseItem) => {
  let score = 70;

  switch (c.incident_type) {
    case 'Sexual Abuse':
    case 'Child Trafficking':
    case 'Online Sexual Exploitation':
      score = 95;
      break;

    case 'Physical Abuse':
      score = 90;
      break;

    case 'Emotional Abuse':
      score = 80;
      break;

    case 'Neglect':
    case 'Child Labor':
    case 'Abandonment':
      score = 75;
      break;

    default:
      score = 70;
  }

  if (String(c.prior_incidents || '').toLowerCase() === 'yes') {
    score += 5;
  }

  if (normalizeStatus(c.priority_level) === 'urgent') {
    score += 3;
  }

  if (normalizeStatus(c.priority_level) === 'emergency') {
    score += 5;
  }

  if (score > 100) {
    score = 100;
  }

  let level = 'low';

  if (score >= 90) {
    level = 'critical';
  } else if (score >= 80) {
    level = 'high';
  } else if (score >= 75) {
    level = 'moderate';
  }

  return {
    score,
    level,
  };
};

const getCaseStatusStyle = (
  status: string | undefined,
  statusTypes: {
    name: string;
    code: string;
    description?: string;
    color?: string;
    active: boolean;
  }[]
) => {
  const normalized = normalizeStatus(status);

  const configuredStatus =
    statusTypes.find(
      (item) =>
        normalizeStatus(item.name) === normalized ||
        normalizeStatus(item.code) === normalized
    );

  const configuredColor =
    normalizeStatus(
      configuredStatus?.color
    );

  switch (configuredColor) {
    case 'red':
    case 'danger':
      return 'bg-red-100 text-red-700 border-red-200';

    case 'orange':
      return 'bg-orange-100 text-orange-700 border-orange-200';

    case 'yellow':
    case 'amber':
    case 'warning':
      return 'bg-amber-100 text-amber-700 border-amber-200';

    case 'green':
    case 'success':
      return 'bg-green-100 text-green-700 border-green-200';

    case 'blue':
    case 'info':
      return 'bg-blue-100 text-blue-700 border-blue-200';

    case 'purple':
      return 'bg-purple-100 text-purple-700 border-purple-200';

    case 'teal':
      return 'bg-teal-100 text-teal-700 border-teal-200';

    case 'gray':
    case 'grey':
      return 'bg-gray-100 text-gray-600 border-gray-200';

    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const getReportStatusStyle = (status?: string) => {
  switch (normalizeStatus(status)) {
    case 'pending':
      return 'bg-blue-100 text-blue-700 border-blue-200';

    case 'in_review':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';

    case 'in_progress':
      return 'bg-purple-100 text-purple-700 border-purple-200';

    case 'resolved':
      return 'bg-green-100 text-green-700 border-green-200';

    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

async function readJsonResponse(res: Response) {
  const text = await res.text();

  if (!text.trim()) {
    throw new Error('The server returned an empty response.');
  }

  try {
    return JSON.parse(text);
  } catch {
    console.error('SERVER RESPONSE:', text);

    throw new Error(
      'Server returned invalid JSON. Check the PHP API for warnings/errors.'
    );
  }
}

export default function SWCasesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [activeTab, setActiveTab] =
    useState<MainTab>('my-cases');

  const [myCases, setMyCases] = useState<CaseItem[]>([]);
  const [caseLoading, setCaseLoading] = useState(true);

  const [caseView, setCaseView] =
    useState<CaseView>('card');

  const [reports, setReports] = useState<ReportItem[]>([]);
  const [reportLoading, setReportLoading] = useState(true);

  const [assessments, setAssessments] = useState<any[]>([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<any>(null);

  const [assessmentSearch, setAssessmentSearch] =
    useState('');

  const [assessmentRiskFilter, setAssessmentRiskFilter] =
    useState('');

  const [assessmentLoading, setAssessmentLoading] =
    useState(true);

  const [workers, setWorkers] = useState<any[]>([]);
  const [reassignModal, setReassignModal] = useState<string | null>(null);
  const [reassignWorker, setReassignWorker] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [caseSearch, setCaseSearch] = useState('');
  const [caseStatusFilter, setCaseStatusFilter] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');

  const [statusTypes, setStatusTypes] = useState<
    {
      name: string;
      code: string;
      description?: string;
      color?: string;
      active: boolean;
    }[]
  >([]);

  const getConfiguredStatus = (
    status?: string
  ) => {
    if (!status) {
      return null;
    }

    const normalized =
      normalizeStatus(status);

    return (
      statusTypes.find(
        (item) =>
          normalizeStatus(item.name) === normalized ||
          normalizeStatus(item.code) === normalized
      ) || null
    );
  };

  const [loadingStatusTypes, setLoadingStatusTypes] =
  useState(true);

  const [showStatusModal, setShowStatusModal] =
    useState<string | null>(null);

  const [statusNote, setStatusNote] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const [showSubmitModal, setShowSubmitModal] =
    useState(false);

  const [submitCaseNumber, setSubmitCaseNumber] =
    useState('');

  const [submitType, setSubmitType] =
    useState('intervention_plan');

  const [submitDesc, setSubmitDesc] =
    useState('');

  const [submitAmount, setSubmitAmount] =
    useState('');

  const [interventionCompleted, setInterventionCompleted] =
    useState(false);

  const [childSafetyAssessed, setChildSafetyAssessed] =
    useState(false);

  const [submitted, setSubmitted] =
    useState(false);

  const [notif, setNotif] = useState('');

  const fetchMyCases = async () => {
    if (!user?.uid) {
      setMyCases([]);
      setCaseLoading(false);
      return;
    }

    try {
      setCaseLoading(true);

      const res = await fetch(
        `${API_BASE}/get_cases.php?uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const data = await readJsonResponse(res);

      if (Array.isArray(data)) {
        setMyCases(data);
      } else if (Array.isArray(data?.data)) {
        setMyCases(data.data);
      } else {
        console.error('Unexpected cases response:', data);
        setMyCases([]);
      }
    } catch (error) {
      console.error('Fetch cases error:', error);

      setNotif(
        error instanceof Error
          ? error.message
          : 'Unable to load cases.'
      );

      setTimeout(() => setNotif(''), 4000);
    } finally {
      setCaseLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setReportLoading(true);

      if (!user?.uid) {
        console.error(
          'Cannot fetch victim reports: logged-in user UID is missing.'
        );

        setReports([]);

        setNotif(
          'Unable to identify the logged-in social worker.'
        );

        setTimeout(() => {
          setNotif('');
        }, 4000);

        return;
      }


      const url =
        `${API_BASE}/get_victim_reports.php?uid=${encodeURIComponent(
          user.uid
        )}`;

      console.log(
        'Fetching victim reports for Social Worker UID:',
        user.uid
      );

      console.log(
        'Victim reports URL:',
        url
      );

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });


      const data = await readJsonResponse(res);

      console.log(
        'VICTIM REPORTS RESPONSE:',
        data
      );

      if (!res.ok) {
        throw new Error(
          data?.error ||
          data?.message ||
          `HTTP Error ${res.status}`
        );
      }


      if (
        data &&
        typeof data === 'object' &&
        data.success === false
      ) {
        throw new Error(
          data.error ||
          data.message ||
          'Unable to load victim reports.'
        );
      }


      if (Array.isArray(data)) {

        console.log(
          'Victim reports loaded:',
          data.length
        );

        setReports(data);

        return;
      }


      if (
        data &&
        typeof data === 'object' &&
        Array.isArray(data.data)
      ) {

        console.log(
          'Victim reports loaded:',
          data.data.length
        );

        setReports(data.data);

        return;
      }

      console.error(
        'Unexpected victim reports response:',
        data
      );

      setReports([]);

      setNotif(
        'No victim reports were returned.'
      );

      setTimeout(() => {
        setNotif('');
      }, 4000);

    } catch (error) {

      console.error(
        'Fetch victim reports error:',
        error
      );

      setReports([]);

      setNotif(
        error instanceof Error
          ? error.message
          : 'Unable to load raised concerns.'
      );

      setTimeout(() => {
        setNotif('');
      }, 4000);

    } finally {

      setReportLoading(false);

    }
  };


  const fetchAssessments = async () => {
    try {
      setAssessmentLoading(true);

      if (!user?.uid) {
        console.error(
          'Cannot fetch assessments: Social Worker UID is missing.'
        );

        setAssessments([]);
        setSelectedAssessment(null);

        return;
      }

      const url =
        `${API_BASE}/get_child_assessments.php?uid=${encodeURIComponent(
          user.uid
        )}`;

      console.log(
        '========================================'
      );

      console.log(
        'FETCHING ASSESSMENT RESULTS'
      );

      console.log(
        'Social Worker UID:',
        user.uid
      );

      console.log(
        'Assessment API:',
        url
      );

      console.log(
        '========================================'
      );
      const res = await fetch(url, {
        method: 'GET',

        headers: {
          Accept: 'application/json',
        },
      });

      const data = await readJsonResponse(res);

      console.log(
        'ASSESSMENT API RESPONSE:',
        data
      );
      if (!res.ok) {
        throw new Error(
          data?.error ||
          data?.message ||
          `HTTP Error ${res.status}`
        );
      }
      if (
        data &&
        typeof data === 'object' &&
        data.success === false
      ) {
        throw new Error(
          data.error ||
          data.message ||
          'Failed to load assessment results.'
        );
      }
      if (
        data &&
        typeof data === 'object' &&
        Array.isArray(data.data)
      ) {

        console.log(
          'ASSESSMENTS FOUND:',
          data.data.length
        );

        console.log(
          'ASSESSMENT DATA:',
          data.data
        );

        setAssessments(data.data);

        if (data.data.length > 0) {

          setSelectedAssessment(
            data.data[0]
          );

        } else {

          setSelectedAssessment(null);

        }

        return;
      }

      if (Array.isArray(data)) {

        console.log(
          'ASSESSMENTS FOUND:',
          data.length
        );

        setAssessments(data);

        if (data.length > 0) {

          setSelectedAssessment(
            data[0]
          );

        } else {

          setSelectedAssessment(null);

        }

        return;
      }

      console.error(
        'Unexpected assessment response:',
        data
      );

      setAssessments([]);
      setSelectedAssessment(null);

      throw new Error(
        'Invalid assessment API response.'
      );

    } catch (error) {

      console.error(
        'Fetch assessment results error:',
        error
      );

      setAssessments([]);
      setSelectedAssessment(null);

      setNotif(
        error instanceof Error
          ? error.message
          : 'Unable to load assessment results.'
      );

      setTimeout(
        () => setNotif(''),
        4000
      );

    } finally {

      setAssessmentLoading(false);

    }
  };

  const filteredAssessments = useMemo(() => {
    return assessments.filter((item) => {

      const keyword =
        assessmentSearch
          .toLowerCase()
          .trim();

      const fullName = `
        ${item.first_name || ''}
        ${item.middle_name || ''}
        ${item.last_name || ''}
      `
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const matchSearch =
        !keyword ||
        item.case_number
          ?.toLowerCase()
          .includes(keyword) ||
        fullName.includes(keyword) ||
        item.risk_level
          ?.toLowerCase()
          .includes(keyword);

      const matchRisk =
        !assessmentRiskFilter ||
        item.risk_level
          ?.toLowerCase()
          .includes(
            assessmentRiskFilter.toLowerCase()
          );

      return matchSearch && matchRisk;
    });
  }, [
    assessments,
    assessmentSearch,
    assessmentRiskFilter,
  ]);

  const totalAssessments =
    assessments.length;

  const criticalAssessments =
    assessments.filter((a) =>
      String(a.risk_level || '')
        .toLowerCase()
        .includes('critical')
    ).length;

  const highAssessments =
    assessments.filter((a) =>
      String(a.risk_level || '')
        .toLowerCase()
        .includes('high')
    ).length;

  const completedAssessments =
    assessments.filter(
      (a) =>
        normalizeStatus(a.status) ===
        'completed'
    ).length;


  const parseAssessmentJson = (
    value: any,
    fallback: any
  ) => {
    if (!value) {
      return fallback;
    }

    if (Array.isArray(value) || typeof value === 'object') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const getAssessmentFullName = (
    item: any
  ) => {
    return `
      ${item.first_name || ''}
      ${item.middle_name || ''}
      ${item.last_name || ''}
    `
      .replace(/\s+/g, ' ')
      .trim() || 'No child name';
  };

  const getAssessmentRiskStyle = (
    risk: string
  ) => {
    const value =
      String(risk || '').toLowerCase();

    if (value.includes('critical')) {
      return 'bg-red-100 text-red-700';
    }

    if (value.includes('high')) {
      return 'bg-orange-100 text-orange-700';
    }

    if (value.includes('moderate')) {
      return 'bg-yellow-100 text-yellow-700';
    }

    return 'bg-green-100 text-green-700';
  };

  useEffect(() => {
    const loadStatusTypes = async () => {
      try {
        setLoadingStatusTypes(true);

        const response = await fetch(
          `${API_BASE}/get_system_config.php`
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}`
          );
        }

        const result = await response.json();

        if (
          !result.success ||
          !Array.isArray(result.data)
        ) {
          throw new Error(
            result.error ||
              'Invalid system configuration response'
          );
        }

        const statusConfig = result.data.find(
          (config: any) =>
            config.config_key === 'status_types'
        );

        if (!statusConfig) {
          setStatusTypes([]);
          return;
        }

        const statuses = Array.isArray(
          statusConfig.config_value
        )
          ? statusConfig.config_value
              .map((item: any) => {
                if (typeof item === 'string') {
                  return {
                    name: item,
                    code: item
                      .toUpperCase()
                      .replace(/[^A-Z0-9]+/g, '_')
                      .replace(/^_+|_+$/g, ''),
                    description: '',
                    color: '',
                    active: true,
                  };
                }

                return {
                  name: item.name || '',
                  code:
                    item.code ||
                    String(item.name || '')
                      .toUpperCase()
                      .replace(/[^A-Z0-9]+/g, '_')
                      .replace(/^_+|_+$/g, ''),
                  description:
                    item.description || '',
                  color: item.color || '',
                  active: item.active !== false,
                };
              })
              .filter(
                (status: {
                  name: string;
                  code: string;
                  active: boolean;
                }) =>
                  status.name &&
                  status.active
              )
          : [];

        setStatusTypes(statuses);
      } catch (error) {
        console.error(
          'Failed to load status types:',
          error
        );

        setStatusTypes([]);
      } finally {
        setLoadingStatusTypes(false);
      }
    };

    loadStatusTypes();
  }, []);

  useEffect(() => {
    fetch('https://guardiancloudcare.com/backend/api/get_social_workers.php')
      .then(async (res) => {
        const data = await res.json();

        if (Array.isArray(data)) {
          setWorkers(data);
        } else {
          setWorkers([]);
        }
      })
      .catch((error) => {
        console.error(
          'Failed to load social workers:',
          error
        );

        setWorkers([]);
      });
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    fetchMyCases();
    fetchReports();
    fetchAssessments();
  }, [user?.uid]);

    useEffect(() => {
      const caseNumber = searchParams.get('case');

      if (!caseNumber || myCases.length === 0) {
        return;
      }

      const matchingCase = myCases.find(
        (caseItem) =>
          String(caseItem.case_number || '').toLowerCase() ===
          caseNumber.toLowerCase()
      );

      setActiveTab('my-cases');
      setCaseSearch(
        matchingCase?.case_number || caseNumber
      );
    }, [searchParams, myCases]);

  const filteredMyCases = useMemo(() => {
    const keyword = caseSearch.toLowerCase().trim();

    return myCases.filter((c) => {
      const childName = getChildName(c).toLowerCase();

      const matchSearch =
        !keyword ||
        String(c.case_number || '')
          .toLowerCase()
          .includes(keyword) ||
        childName.includes(keyword) ||
        String(c.incident_type || '')
          .toLowerCase()
          .includes(keyword) ||
        String(c.incident_location || '')
          .toLowerCase()
          .includes(keyword) ||
        String(c.location || '')
          .toLowerCase()
          .includes(keyword);

      const matchStatus =
        !caseStatusFilter ||
        normalizeStatus(c.case_status) ===
          normalizeStatus(caseStatusFilter);

      const matchType =
        !caseTypeFilter ||
        String(c.incident_type || '') ===
          caseTypeFilter;

      return (
        matchSearch &&
        matchStatus &&
        matchType
      );
    });
  }, [
    myCases,
    caseSearch,
    caseStatusFilter,
    caseTypeFilter,
  ]);

  const caseTypeOptions = Array.from(
    new Set(
      myCases
        .map((c) => c.incident_type)
        .filter(Boolean)
    )
  ).sort();

  const totalCases = myCases.length;

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    statusTypes.forEach((status) => {
      counts[status.code] = myCases.filter(
        (c) =>
          normalizeStatus(c.case_status) ===
          normalizeStatus(status.name)
      ).length;
    });

    return counts;
  }, [myCases, statusTypes]);

  const totalReports = reports.length;

  const pendingReports = reports.filter(
    (r) => normalizeStatus(r.status) === 'pending'
  ).length;

  const reviewReports = reports.filter(
    (r) =>
      normalizeStatus(r.status) === 'in_review'
  ).length;

  const progressReports = reports.filter(
    (r) =>
      normalizeStatus(r.status) === 'in_progress'
  ).length;

  const resolvedReports = reports.filter(
    (r) =>
      normalizeStatus(r.status) === 'resolved'
  ).length;

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const keyword = search.toLowerCase().trim();

      const childName =
        getReportChildName(report).toLowerCase();

      const matchSearch =
        !keyword ||
        report.report_uid
          ?.toLowerCase()
          .includes(keyword) ||
        report.incident_type
          ?.toLowerCase()
          .includes(keyword) ||
        report.location
          ?.toLowerCase()
          .includes(keyword) ||
        report.reporter_name
          ?.toLowerCase()
          .includes(keyword) ||
        childName.includes(keyword);

      const matchStatus =
        !statusFilter ||
        normalizeStatus(report.status) ===
          normalizeStatus(statusFilter);

      const matchType =
        !typeFilter ||
        report.incident_type === typeFilter;

      return (
        matchSearch &&
        matchStatus &&
        matchType
      );
    });
  }, [
    reports,
    search,
    statusFilter,
    typeFilter,
  ]);

  const handleStatusUpdate = async () => {
    if (!showStatusModal || !newStatus) {
      return;
    }

    if (!user?.uid) {
      setNotif('User session not found.');
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/update_case.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_number: showStatusModal,
            case_status: newStatus,
            progress_note: statusNote,
            uid: user.uid,
          }),
        }
      );

      const data = await readJsonResponse(res);

      if (!data.success) {
        throw new Error(
          data.error ||
            data.message ||
            'Failed to update case.'
        );
      }

      setNotif(
        'Case updated successfully!'
      );

      setShowStatusModal(null);
      setStatusNote('');
      setNewStatus('');

      await fetchMyCases();
    } catch (error) {
      console.error(
        'Update case error:',
        error
      );

      setNotif(
        error instanceof Error
          ? error.message
          : 'Failed to update case.'
      );
    }

    setTimeout(() => setNotif(''), 3000);
  };

  const updateReportStatus = async (
    reportUid: string,
    status: string
  ) => {
    try {
      const res = await fetch(
        `${API_BASE}/update_victim_report_status.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            report_uid: reportUid,
            status,
          }),
        }
      );

      const data = await readJsonResponse(res);

      if (!data.success) {
        throw new Error(
          data.error ||
            data.message ||
            'Failed to update report.'
        );
      }

      setNotif(
        'Concern status updated successfully!'
      );

      await fetchReports();
    } catch (error) {
      console.error(
        'Update report status error:',
        error
      );

      setNotif(
        error instanceof Error
          ? error.message
          : 'Failed to update concern.'
      );
    }

    setTimeout(() => setNotif(''), 3000);
  };

  const handleSubmitApproval = async () => {
    if (!submitCaseNumber) {
      return;
    }

    if (submitType === 'case_closure') {
      if (!interventionCompleted) {
        setNotif('Please confirm intervention completion.');
        return;
      }

      if (!childSafetyAssessed) {
        setNotif('Please confirm the child safety assessment.');
        return;
      }
    }

    if (!submitDesc.trim()) {
      setNotif(
        'Please enter a description.'
      );
      return;
    }

    if (
      submitType === 'financial_assistance' &&
      !submitAmount
    ) {
      setNotif(
        'Please enter the amount requested.'
      );
      return;
    }

    if (!user?.uid) {
      setNotif('User session not found.');
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/submit_case.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_number: submitCaseNumber,
            request_type: submitType,
            request_type_description: submitDesc,
            amount_requested:
              submitType ===
              'financial_assistance'
                ? submitAmount
                : null,
            uid: user.uid,
            intervention_completed: interventionCompleted,
            child_safety_assessed: childSafetyAssessed
          }),
        }
      );

      const data = await readJsonResponse(res);

      if (!data.success && !data.message) {
        throw new Error(
          data.error ||
            'Failed to submit case.'
        );
      }

      setSubmitted(true);

      await fetchMyCases();
    } catch (error) {
      console.error(
        'Submit case error:',
        error
      );

      setNotif(
        error instanceof Error
          ? error.message
          : 'Failed to submit case.'
      );
    }

    setTimeout(() => setNotif(''), 3000);
  };

  const closeSubmitModal = () => {
    setShowSubmitModal(false);
    setSubmitted(false);
    setSubmitCaseNumber('');
    setSubmitDesc('');
    setSubmitAmount('');
    setSubmitType('intervention_plan');
    setInterventionCompleted(false);
    setChildSafetyAssessed(false);
  };

  return (
    <AppLayout
      title="My Cases"
      breadcrumb={[
        'Social Worker',
        'My Cases',
      ]}
    >
      <div className="space-y-6">

        {notif && (
          <div className="fixed top-5 right-5 z-[100] bg-teal-500 text-white shadow-xl px-5 py-3 rounded-xl text-sm max-w-md">
            <div className="flex items-center gap-2">
              <i className="ri-information-line" />

              <span>{notif}</span>

              <button
                onClick={() => setNotif('')}
                className="ml-2 text-white/80 hover:text-white"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

          <div>
            <h1 className="text-3xl font-bold text-[#1f2a5a]">
              My Cases
            </h1>

            <p className="text-gray-500 mt-1">
              Manage assigned cases and raised
              concerns.
            </p>
          </div>

          <button
            onClick={() =>
              navigate('/case-intake')
            }
            className="bg-teal-500 text-white text-sm px-5 py-3 rounded-xl hover:bg-teal-600 transition flex items-center justify-center gap-2"
          >
            <i className="ri-add-line" />
            New Case Intake
          </button>

        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5 flex">

          <button
            onClick={() =>
              setActiveTab('my-cases')
            }
            className={`
              flex-1
              py-3
              rounded-xl
              text-sm
              font-semibold
              transition
              flex
              items-center
              justify-center
              gap-2
              ${
                activeTab === 'my-cases'
                  ? 'bg-teal-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50'
              }
            `}
          >
            <i className="ri-folder-user-line" />
            My Cases
          </button>

          <button
            onClick={() =>
              setActiveTab('raised-concerns')
            }
            className={`
              flex-1
              py-3
              rounded-xl
              text-sm
              font-semibold
              transition
              flex
              items-center
              justify-center
              gap-2
              ${
                activeTab ===
                'raised-concerns'
                  ? 'bg-teal-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50'
              }
            `}
          >
            <i className="ri-alarm-warning-line" />
            Raised Concerns
          </button>

          <button
            onClick={() =>
              setActiveTab('assessment-results')
            }
            className={`
              flex-1
              py-3
              rounded-xl
              text-sm
              font-semibold
              transition
              flex
              items-center
              justify-center
              gap-2
              ${
                activeTab === 'assessment-results'
                  ? 'bg-teal-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50'
              }
            `}
          >
            <i className="ri-file-chart-line" />
            Assessment Results
          </button>

          <button
            onClick={() => setActiveTab('referrals')}
            className={`
              flex-1
              py-3
              rounded-xl
              text-sm
              font-semibold
              transition
              flex
              items-center
              justify-center
              gap-2
              ${
                activeTab === 'referrals'
                  ? 'bg-teal-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50'
              }
            `}
          >
            <i className="ri-user-shared-line" />
            Referrals
          </button>

        </div>

        {activeTab === 'my-cases' && (
          <>

            {/* STATUS SUMMARY */}

            <div
              className={`
                grid
                grid-cols-2
                md:grid-cols-3
                xl:grid-cols-6
                gap-4
              `}
            >
              <CaseStatusCard
                title="Total"
                value={totalCases}
                icon="ri-folder-line"
                active
              />

              {statusTypes.map((status) => (
                <CaseStatusCard
                  key={status.code}
                  title={status.name}
                  value={statusCounts[status.code] || 0}
                  icon="ri-folder-line"
                />
              ))}
            </div>

            {/* CASES HEADER */}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  My Cases
                </h2>

                <p className="text-sm text-gray-500">
                  Cases currently assigned to you
                </p>
              </div>

              {/* VIEW SWITCH */}

              <div className="bg-white border border-gray-200 rounded-xl p-1 flex gap-1">

                <button
                  onClick={() =>
                    setCaseView('card')
                  }
                  className={`
                    px-4 py-2 rounded-lg
                    text-sm flex items-center gap-2
                    ${
                      caseView === 'card'
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }
                  `}
                >
                  <i className="ri-grid-line" />
                  Cards
                </button>

                <button
                  onClick={() =>
                    setCaseView('table')
                  }
                  className={`
                    px-4 py-2 rounded-lg
                    text-sm flex items-center gap-2
                    ${
                      caseView === 'table'
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }
                  `}
                >
                  <i className="ri-list-check" />
                  Table
                </button>

              </div>

            </div>

            {/* CASE SEARCH & FILTERS */}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">

                {/* SEARCH */}

                <div className="relative xl:col-span-2">

                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                  <input
                    type="text"
                    value={caseSearch}
                    onChange={(e) =>
                      setCaseSearch(e.target.value)
                    }
                    placeholder="Search case number, child, incident, or location..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition"
                  />

                </div>

                {/* STATUS */}

                <select
                  value={caseStatusFilter}
                  onChange={(e) =>
                    setCaseStatusFilter(e.target.value)
                  }
                  className="px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition bg-white"
                >
                  <option value="">
                    All Statuses
                  </option>

                  {statusTypes.map((status) => (
                    <option
                      key={status.code}
                      value={status.name}
                    >
                      {status.name}
                    </option>
                  ))}
                </select>

                {/* INCIDENT TYPE */}

                <select
                  value={caseTypeFilter}
                  onChange={(e) =>
                    setCaseTypeFilter(e.target.value)
                  }
                  className="px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition bg-white"
                >
                  <option value="">
                    All Incident Types
                  </option>

                  {caseTypeOptions.map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  ))}

                </select>

              </div>

              {/* FILTER SUMMARY */}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">

                <p className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-semibold text-gray-800">
                    {filteredMyCases.length}
                  </span>{' '}
                  of{' '}
                  <span className="font-semibold text-gray-800">
                    {myCases.length}
                  </span>{' '}
                  cases
                </p>

                {(caseSearch ||
                  caseStatusFilter ||
                  caseTypeFilter) && (
                  <button
                    type="button"
                    onClick={() => {
                      setCaseSearch('');
                      setCaseStatusFilter('');
                      setCaseTypeFilter('');
                    }}
                    className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-2"
                  >
                    <i className="ri-close-circle-line" />
                    Clear Filters
                  </button>
                )}

              </div>

            </div>

            {/* LOADING */}

            {caseLoading ? (
              <LoadingState text="Loading cases..." />
            ) : filteredMyCases.length === 0 ? (
              <EmptyState
                icon="ri-folder-search-line"
                title="No Cases Found"
                description={
                  myCases.length === 0
                    ? "There are currently no cases assigned to your account."
                    : "No cases match your current search or filters."
                }
              />
            ) : caseView === 'card' ? (

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

                {filteredMyCases.map((c) => {
                  const risk = getRisk(c);

                  return (
                    <CaseCard
                      key={c.case_number}
                      caseItem={c}
                      risk={risk}
                      statusTypes={statusTypes}
                      onView={() =>
                        navigate(
                          `/child-profiles/${c.case_number}`
                        )
                      }
                      onUpdate={() => {
                        setShowStatusModal(
                          c.case_number
                        );
                        setNewStatus('');
                        setStatusNote(
                          c.progress_note || ''
                        );
                      }}
                      onSubmit={() => {
                        setSubmitCaseNumber(
                          c.case_number
                        );
                        setSubmitted(false);
                        setShowSubmitModal(true);
                      }}
                    />
                  );
                })}

              </div>

            ) : (


              <CaseTable
                cases={filteredMyCases}
                statusTypes={statusTypes}
                onView={(caseNumber) =>
                  navigate(
                    `/child-profiles/${caseNumber}`
                  )
                }
                onUpdate={(caseNumber) => {
                  const selected = myCases.find(
                    (c) =>
                      c.case_number ===
                      caseNumber
                  );

                  setShowStatusModal(
                    caseNumber
                  );

                  setNewStatus('');

                  setStatusNote(
                    selected?.progress_note || ''
                  );
                }}
                onSubmit={(caseNumber) => {
                  setSubmitCaseNumber(
                    caseNumber
                  );

                  setSubmitted(false);
                  setShowSubmitModal(true);
                }}
              />

            )}

          </>
        )}

        {activeTab === 'raised-concerns' && (
          <>

            {/* RAISED CONCERN SUMMARY */}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">

              <ReportStatusCard
                title="Total"
                value={totalReports}
                icon="ri-file-list-3-line"
                active
              />

              <ReportStatusCard
                title="New"
                value={pendingReports}
                icon="ri-time-line"
              />

              <ReportStatusCard
                title="In Review"
                value={reviewReports}
                icon="ri-search-eye-line"
              />

              <ReportStatusCard
                title="In Progress"
                value={progressReports}
                icon="ri-loader-4-line"
              />

              <ReportStatusCard
                title="Resolved"
                value={resolvedReports}
                icon="ri-checkbox-circle-line"
              />

            </div>

            {/* RAISED CONCERNS HEADER */}

            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Raised Concerns
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                View and manage concerns submitted
                by victims and the public.
              </p>
            </div>

            {/* FILTERS */}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

                <div className="relative md:col-span-2">

                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                  <input
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                    placeholder="Search by ID, child, type, location, or reporter..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500"
                  />

                </div>

                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value
                    )
                  }
                  className="px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500"
                >
                  <option value="">
                    All Status
                  </option>

                  <option value="pending">
                    New
                  </option>

                  <option value="in_review">
                    In Review
                  </option>

                  <option value="in_progress">
                    In Progress
                  </option>

                  <option value="resolved">
                    Resolved
                  </option>
                </select>

                <select
                  value={typeFilter}
                  onChange={(e) =>
                    setTypeFilter(
                      e.target.value
                    )
                  }
                  className="px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500"
                >
                  <option value="">
                    All Types
                  </option>

                  {[
                    ...new Set(
                      reports
                        .map(
                          (r) =>
                            r.incident_type
                        )
                        .filter(Boolean)
                    ),
                  ].map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  ))}

                </select>

              </div>

            </div>

            {/* REPORT TABLE */}

            {reportLoading ? (
              <LoadingState text="Loading raised concerns..." />
            ) : filteredReports.length === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title="No Raised Concerns"
                description="No reports match your current filters."
              />
            ) : (

              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">

                <div className="overflow-x-auto">

                  <table className="w-full min-w-[1200px]">

                    <thead className="bg-gray-50 border-b border-gray-100">

                      <tr className="text-left text-sm text-gray-500">

                        <th className="px-5 py-4">
                          ID
                        </th>

                        <th className="px-5 py-4">
                          Child / Victim
                        </th>

                        <th className="px-5 py-4">
                          Type of Concern
                        </th>

                        <th className="px-5 py-4">
                          Reported By
                        </th>

                        <th className="px-5 py-4">
                          Location
                        </th>

                        <th className="px-5 py-4">
                          Date
                        </th>

                        <th className="px-5 py-4">
                          Status
                        </th>

                        <th className="px-5 py-4">
                          Action
                        </th>

                      </tr>

                    </thead>

                    <tbody>

                      {filteredReports.map(
                        (report) => (

                          <tr
                            key={report.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >

                            {/* ID */}

                            <td className="px-5 py-4">

                              <p className="text-sm font-semibold text-[#5f6ea5]">
                                {report.report_uid}
                              </p>

                              <p className="text-xs text-gray-400">
                                Victim UID: {report.victim_uid}
                              </p>

                            </td>

                            {/* CHILD */}

                            <td className="px-5 py-4">

                              <div className="flex items-center gap-3">

                                <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">

                                  <i className="ri-user-line text-orange-500 text-lg" />

                                </div>

                                <div>

                                  <p className="font-semibold text-gray-800">
                                    {getReportChildName(
                                      report
                                    )}
                                  </p>

                                  <p className="text-xs text-gray-400">
                                    {report.age
                                      ? `${report.age} years`
                                      : 'Age not provided'}
                                    {report.gender
                                      ? ` · ${report.gender}`
                                      : ''}
                                  </p>

                                </div>

                              </div>

                            </td>

                            {/* TYPE */}

                            <td className="px-5 py-4">

                              <div className="flex items-center gap-2">

                                <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">

                                  <i className="ri-alarm-warning-line text-red-500" />

                                </div>

                                <div>

                                  <p className="font-semibold text-gray-800">
                                    {report.incident_type}
                                  </p>

                                  <p className="text-xs text-gray-400">
                                    {report.urgency || 'Standard'}
                                  </p>

                                </div>

                              </div>

                            </td>

                            {/* REPORTER */}

                            <td className="px-5 py-4">

                              <p className="font-medium text-gray-800">

                                {report.is_anonymous ===
                                '1'
                                  ? 'Anonymous'
                                  : report.reporter_name ||
                                    'Not provided'}

                              </p>

                              <p className="text-xs text-gray-400">
                                {report.reporter_contact ||
                                  'No contact'}
                              </p>

                            </td>

                            {/* LOCATION */}

                            <td className="px-5 py-4 text-sm text-gray-600 max-w-[220px]">

                              <p className="truncate">
                                {report.location ||
                                  'Not provided'}
                              </p>

                            </td>

                            {/* DATE */}

                            <td className="px-5 py-4 text-sm text-gray-500">
                              {formatDate(
                                report.created_at
                              )}
                            </td>

                            {/* STATUS */}

                            <td className="px-5 py-4">

                              <select
                                value={
                                  report.status ||
                                  ''
                                }
                                onChange={(e) =>
                                  updateReportStatus(
                                    report.report_uid,
                                    e.target.value
                                  )
                                }
                                className={`
                                  px-3
                                  py-2
                                  rounded-full
                                  text-xs
                                  font-semibold
                                  border
                                  outline-none
                                  cursor-pointer
                                  ${getReportStatusStyle(
                                    report.status
                                  )}
                                `}
                              >

                                <option value="pending">
                                  New
                                </option>

                                <option value="in_review">
                                  In Review
                                </option>

                                <option value="in_progress">
                                  In Progress
                                </option>

                                <option value="resolved">
                                  Resolved
                                </option>

                              </select>

                            </td>

                            {/* ACTION */}

                            <td className="px-5 py-4">

                              <button
                                onClick={() =>
                                  navigate(
                                    `/sw-raise-concern/${report.report_uid}`
                                  )
                                }
                                className="w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50 transition"
                                title="View Concern"
                              >

                                <i className="ri-eye-line" />

                              </button>

                            </td>

                          </tr>

                        )
                      )}

                    </tbody>

                  </table>

                </div>

              </div>

            )}

          </>
        )}

        {/* ===================================================
            ASSESSMENT RESULTS
        =================================================== */}

        {activeTab === 'assessment-results' && (
          <>

            {/* HEADER */}

            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Assessment Results
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                View all saved child assessments and
                intervention plans.
              </p>
            </div>

            {/* STATISTICS */}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

              <AssessmentStatCard
                icon="ri-file-list-3-line"
                title="Total Assessments"
                value={totalAssessments}
                color="blue"
              />

              <AssessmentStatCard
                icon="ri-alarm-warning-line"
                title="Critical Risk"
                value={criticalAssessments}
                color="red"
              />

              <AssessmentStatCard
                icon="ri-alert-line"
                title="High Risk"
                value={highAssessments}
                color="orange"
              />

              <AssessmentStatCard
                icon="ri-checkbox-circle-line"
                title="Completed"
                value={completedAssessments}
                color="green"
              />

            </div>

            {/* CONTENT */}

            {assessmentLoading ? (

              <LoadingState
                text="Loading assessment results..."
              />

            ) : (

              <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">

                {/* =================================================
                    ASSESSMENT TABLE
                ================================================= */}

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

                  {/* FILTER */}

                  <div className="p-5 border-b border-gray-100">

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                      <div className="relative md:col-span-2">

                        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                        <input
                          value={assessmentSearch}
                          onChange={(e) =>
                            setAssessmentSearch(
                              e.target.value
                            )
                          }
                          placeholder="Search case number, child name, or risk level..."
                          className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 outline-none focus:border-teal-500"
                        />

                      </div>

                      <select
                        value={assessmentRiskFilter}
                        onChange={(e) =>
                          setAssessmentRiskFilter(
                            e.target.value
                          )
                        }
                        className="px-4 py-3 rounded-2xl border border-gray-200 outline-none focus:border-teal-500"
                      >

                        <option value="">
                          All Risk Levels
                        </option>

                        <option value="critical">
                          Critical Risk
                        </option>

                        <option value="high">
                          High Risk
                        </option>

                        <option value="moderate">
                          Moderate Risk
                        </option>

                        <option value="low">
                          Low Risk
                        </option>

                      </select>

                    </div>

                  </div>

                  {/* TABLE */}

                  <div className="overflow-x-auto">

                    <table className="w-full min-w-[1000px]">

                      <thead className="bg-gray-50 border-b border-gray-100">

                        <tr className="text-left text-sm text-gray-500">

                          <th className="px-5 py-4">
                            Case Number
                          </th>

                          <th className="px-5 py-4">
                            Child
                          </th>

                          <th className="px-5 py-4">
                            Incident Type
                          </th>

                          <th className="px-5 py-4">
                            Risk Level
                          </th>

                          <th className="px-5 py-4">
                            Score
                          </th>

                          <th className="px-5 py-4">
                            Status
                          </th>

                          <th className="px-5 py-4">
                            Date
                          </th>

                          <th className="px-5 py-4">
                            Action
                          </th>

                        </tr>

                      </thead>

                      <tbody>

                        {filteredAssessments.length ===
                        0 ? (

                          <tr>

                            <td
                              colSpan={8}
                              className="text-center text-gray-400 py-10"
                            >
                              No assessment results
                              found.
                            </td>

                          </tr>

                        ) : (

                          filteredAssessments.map(
                            (item) => (

                              <tr
                                key={item.id}
                                className="border-b border-gray-100 hover:bg-gray-50"
                              >

                                {/* CASE */}

                                <td className="px-5 py-4">

                                  <p className="text-sm font-semibold text-teal-700">
                                    {item.case_number}
                                  </p>

                                </td>

                                {/* CHILD */}

                                <td className="px-5 py-4">

                                  <div>

                                    <p className="font-semibold text-gray-800">

                                      {getAssessmentFullName(
                                        item
                                      )}

                                    </p>

                                    <p className="text-xs text-gray-400">

                                      {item.age || '-'} yrs
                                      {' • '}
                                      {item.gender || '-'}

                                    </p>

                                  </div>

                                </td>

                                {/* INCIDENT */}

                                <td className="px-5 py-4 text-sm text-gray-600">

                                  {item.incident_type ||
                                    'No incident'}

                                </td>

                                {/* RISK */}

                                <td className="px-5 py-4">

                                  <span
                                    className={`
                                      px-3
                                      py-1
                                      rounded-full
                                      text-xs
                                      font-semibold
                                      ${getAssessmentRiskStyle(
                                        item.risk_level
                                      )}
                                    `}
                                  >
                                    {item.risk_level ||
                                      'Unknown'}
                                  </span>

                                </td>

                                {/* SCORE */}

                                <td className="px-5 py-4">

                                  <span className="font-bold text-gray-800">

                                    {item.risk_score ??
                                      0}%

                                  </span>

                                </td>

                                {/* STATUS */}

                                <td className="px-5 py-4">

                                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">

                                    {formatStatus(
                                      item.status
                                    )}

                                  </span>

                                </td>

                                {/* DATE */}

                                <td className="px-5 py-4 text-sm text-gray-500">

                                  {formatDate(
                                    item.created_at
                                  )}

                                </td>

                                {/* ACTION */}

                                <td className="px-5 py-4">

                                  <button
                                    onClick={() =>
                                      setSelectedAssessment(
                                        item
                                      )
                                    }
                                    className="w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50"
                                    title="View Assessment"
                                  >

                                    <i className="ri-eye-line" />

                                  </button>

                                </td>

                              </tr>

                            )
                          )

                        )}

                      </tbody>

                    </table>

                  </div>

                </div>

                {/* =================================================
                    SELECTED ASSESSMENT
                ================================================= */}

                {selectedAssessment && (

                  <div className="space-y-5">

                    {/* CHILD HEADER */}

                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                      <div className="flex items-start justify-between gap-3">

                        <div>

                          <h2 className="text-xl font-bold text-gray-900">

                            {getAssessmentFullName(
                              selectedAssessment
                            )}

                          </h2>

                          <p className="text-sm text-gray-500 mt-1">

                            {selectedAssessment.case_number}
                            {' • '}
                            {selectedAssessment.age ||
                              '-'} yrs
                            {' • '}
                            {selectedAssessment.gender ||
                              '-'}

                          </p>

                        </div>

                        <span
                          className={`
                            px-3
                            py-1
                            rounded-full
                            text-xs
                            font-semibold
                            ${getAssessmentRiskStyle(
                              selectedAssessment.risk_level
                            )}
                          `}
                        >

                          {selectedAssessment.risk_level ||
                            'Unknown'}

                        </span>

                      </div>

                      <div className="mt-4 flex items-center justify-between bg-gray-50 rounded-xl p-3">

                        <span className="text-sm text-gray-500">
                          Risk Score
                        </span>

                        <span className="text-xl font-bold text-gray-900">

                          {selectedAssessment.risk_score ??
                            0}%

                        </span>

                      </div>

                    </div>

                    {/* AI SUMMARY */}

                    <AssessmentDetailCard
                      title="AI Summary"
                      icon="ri-brain-line"
                      content={
                        selectedAssessment.ai_summary ||
                        'No summary available.'
                      }
                    />

                    {/* RECOMMENDATIONS */}

                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">

                        <i className="ri-checkbox-circle-line text-teal-600" />

                        Recommendations

                      </h3>

                      <div className="space-y-2">

                        {parseAssessmentJson(
                          selectedAssessment.recommendations,
                          []
                        ).length === 0 ? (

                          <p className="text-sm text-gray-400">
                            No recommendations
                            available.
                          </p>

                        ) : (

                          parseAssessmentJson(
                            selectedAssessment.recommendations,
                            []
                          ).map(
                            (
                              rec: string,
                              index: number
                            ) => (

                              <div
                                key={index}
                                className="flex gap-3 text-sm text-gray-700"
                              >

                                <i className="ri-check-line text-teal-600 mt-0.5" />

                                <span>{rec}</span>

                              </div>

                            )
                          )

                        )}

                      </div>

                    </div>

                    {/* INTERVENTION PLAN */}

                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">

                        <i className="ri-road-map-line text-purple-600" />

                        Intervention Plan

                      </h3>

                      {parseAssessmentJson(
                        selectedAssessment.intervention_plan,
                        []
                      ).length === 0 ? (

                        <p className="text-sm text-gray-400">
                          No intervention plan
                          available.
                        </p>

                      ) : (

                        parseAssessmentJson(
                          selectedAssessment.intervention_plan,
                          []
                        ).map(
                          (
                            plan: any,
                            index: number
                          ) => (

                            <div
                              key={index}
                              className="space-y-4"
                            >

                              {/* GOAL */}

                              <div>

                                <p className="text-xs text-gray-400">
                                  Primary Goal
                                </p>

                                <p className="font-semibold text-gray-800 mt-1">

                                  {plan.primary_goal ||
                                    'No primary goal'}

                                </p>

                              </div>

                              {/* OBJECTIVES */}

                              <div>

                                <p className="text-xs text-gray-400 mb-2">
                                  Objectives
                                </p>

                                {(plan.objectives ||
                                  []).map(
                                  (
                                    obj: string,
                                    i: number
                                  ) => (

                                    <p
                                      key={i}
                                      className="text-sm text-gray-600 mb-1"
                                    >
                                      • {obj}
                                    </p>

                                  )
                                )}

                              </div>

                              {/* STRATEGIES */}

                              <div>

                                <p className="text-xs text-gray-400 mb-2">
                                  Strategies
                                </p>

                                {(plan.strategies ||
                                  []).map(
                                  (
                                    strategy: any,
                                    i: number
                                  ) => (

                                    <div
                                      key={i}
                                      className="bg-gray-50 rounded-xl p-3 mb-2"
                                    >

                                      <p className="font-semibold text-sm text-gray-800">

                                        {strategy.strategy ||
                                          'Strategy'}

                                      </p>

                                      <p className="text-xs text-gray-500 mt-1">

                                        {strategy.description ||
                                          'No description'}

                                      </p>

                                      <p className="text-xs text-gray-400 mt-1">

                                        {strategy.responsible_person ||
                                          'Not assigned'}

                                        {' • '}

                                        {strategy.timeline ||
                                          'No timeline'}

                                      </p>

                                    </div>

                                  )
                                )}

                              </div>

                              {/* NOTES */}

                              {plan.notes && (

                                <div>

                                  <p className="text-xs text-gray-400">
                                    Notes
                                  </p>

                                  <p className="text-sm text-gray-600 mt-1">
                                    {plan.notes}
                                  </p>

                                </div>

                              )}

                            </div>

                          )
                        )

                      )}

                    </div>

                    {/* ASSESSMENT NOTES */}

                    <AssessmentDetailCard
                      title="Assessment Notes"
                      icon="ri-sticky-note-line"
                      content={
                        selectedAssessment.notes ||
                        'No notes provided.'
                      }
                    />

                  </div>

                )}

              </div>

            )}

          </>
        )}

        {activeTab === 'referrals' && (
          <ReferralsSection
            cases={myCases}
            onViewDetails={(caseItem) => {
              navigate(
                `/child-profiles/${caseItem.case_number}`
              );
            }}
            onReassign={(caseNumber) => {
              setReassignModal(caseNumber);
              setReassignWorker('');
            }}
            onMarkPriority={(caseItem) => {
              console.log('Mark priority:', caseItem);
            }}
          />
        )}


        {reassignModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">

            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

              {/* HEADER */}

              <div className="px-6 py-5 border-b border-gray-100">

                <div className="flex items-center gap-3">

                  <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">

                    <i className="ri-user-shared-line text-amber-600 text-xl" />

                  </div>

                  <div>

                    <h3 className="text-lg font-bold text-gray-900">
                      Reassign Case
                    </h3>

                    <p className="text-xs text-gray-500">
                      Assign this victim to another social worker
                    </p>

                  </div>

                </div>

              </div>

              {/* BODY */}

              <div className="p-6">

                <div className="mb-4">

                  <p className="text-xs text-gray-400 mb-1">
                    Case Number
                  </p>

                  <p className="text-sm font-semibold text-teal-700">
                    {reassignModal}
                  </p>

                </div>

                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Assign to Social Worker
                </label>

                <select
                  value={reassignWorker}
                  onChange={(e) =>
                    setReassignWorker(e.target.value)
                  }
                  className="
                    w-full
                    border
                    border-gray-200
                    rounded-xl
                    px-4
                    py-3
                    text-sm
                    outline-none
                    focus:border-teal-500
                    focus:ring-2
                    focus:ring-teal-100
                  "
                >

                  <option value="">
                    Select social worker...
                  </option>

                  {workers.map((worker) => (
                    <option
                      key={worker.uid}
                      value={worker.uid}
                    >
                      {worker.name}
                    </option>
                  ))}

                </select>

              </div>

              {/* FOOTER */}

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">

                <button
                  type="button"
                  onClick={() => {
                    setReassignModal(null);
                    setReassignWorker('');
                  }}
                  className="
                    px-4
                    py-2.5
                    text-sm
                    font-medium
                    text-gray-600
                    border
                    border-gray-200
                    rounded-xl
                    hover:bg-white
                  "
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={
                    !reassignWorker ||
                    reassignLoading
                  }
                  onClick={async () => {

                    if (!reassignModal) {
                      return;
                    }

                    if (!reassignWorker) {
                      return;
                    }

                    try {

                      setReassignLoading(true);

                      const response = await fetch(
                        'https://guardiancloudcare.com/backend/api/reassign_case.php',
                        {
                          method: 'POST',

                          headers: {
                            'Content-Type':
                              'application/json',
                          },

                          body: JSON.stringify({
                            case_number:
                              reassignModal,

                            uid:
                              reassignWorker,
                          }),
                        }
                      );

                      const result =
                        await response.json();

                      if (!result.success) {
                        throw new Error(
                          result.error ||
                          'Failed to reassign case.'
                        );
                      }

                      setReassignModal(null);
                      setReassignWorker('');

                      // Refresh the cases
                      await fetchMyCases();

                      // Optional notification
                      setNotif(
                        'Case reassigned successfully.'
                      );

                      setTimeout(() => {
                        setNotif('');
                      }, 3000);

                    } catch (error) {

                      console.error(
                        'Reassignment error:',
                        error
                      );

                      setNotif(
                        error instanceof Error
                          ? error.message
                          : 'Failed to reassign case.'
                      );

                      setTimeout(() => {
                        setNotif('');
                      }, 4000);

                    } finally {

                      setReassignLoading(false);

                    }

                  }}
                  className="
                    px-5
                    py-2.5
                    text-sm
                    font-semibold
                    text-white
                    bg-teal-500
                    rounded-xl
                    hover:bg-teal-600
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                    flex
                    items-center
                    gap-2
                  "
                >

                  {reassignLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Reassigning...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-shared-line" />
                      Reassign
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>
        )}

      </div>

      {showStatusModal && (

        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[90] p-4">

          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">

            <div className="flex items-center justify-between mb-5">

              <div>

                <h3 className="text-lg font-bold text-gray-900">
                  Update Case Status
                </h3>

                <p className="text-xs text-gray-400 mt-1">
                  {showStatusModal}
                </p>

              </div>

              <button
                onClick={() =>
                  setShowStatusModal(null)
                }
                className="w-9 h-9 rounded-xl hover:bg-gray-100 text-gray-400"
              >
                <i className="ri-close-line text-xl" />
              </button>

            </div>

            {/* STATUS */}

            <div className="mb-5">

              <label className="text-sm font-medium text-gray-700 mb-2 block">
                New Status
              </label>

              <div className="grid grid-cols-2 gap-2">

                {statusTypes.map((status) => (

                  <button
                    key={status.code}
                    onClick={() =>
                      setNewStatus(status.name)
                    }
                    className={`
                      py-3
                      px-3
                      rounded-xl
                      border-2
                      text-sm
                      font-medium
                      transition
                      ${
                        normalizeStatus(newStatus) === normalizeStatus(status.name)
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }
                    `}
                  >
                    {status.name}
                  </button>

                ))}

              </div>

            </div>

            {/* NOTE */}

            <div className="mb-5">

              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Progress Note
              </label>

              <textarea
                value={statusNote}
                onChange={(e) =>
                  setStatusNote(
                    e.target.value
                  )
                }
                rows={4}
                maxLength={500}
                placeholder="Describe the progress update..."
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-teal-500 resize-none"
              />

              <p className="text-xs text-gray-400 text-right mt-1">
                {statusNote.length}/500
              </p>

            </div>

            {/* ACTION */}

            <div className="flex gap-3 justify-end">

              <button
                onClick={() =>
                  setShowStatusModal(null)
                }
                className="text-sm border border-gray-200 px-4 py-2.5 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleStatusUpdate}
                disabled={!newStatus}
                className="text-sm bg-teal-500 text-white px-5 py-2.5 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed"
              >
                Update Status
              </button>

            </div>

          </div>

        </div>

      )}

      {showSubmitModal && (

        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[90] p-4">

          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">

            <div className="flex items-center justify-between mb-5">

              <h3 className="text-lg font-bold text-gray-900">
                Submit for Approval
              </h3>

              <button
                onClick={closeSubmitModal}
                className="w-9 h-9 rounded-xl hover:bg-gray-100 text-gray-400"
              >
                <i className="ri-close-line text-xl" />
              </button>

            </div>

            {submitted ? (

              <div className="text-center py-8">

                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">

                  <i className="ri-checkbox-circle-line text-teal-500 text-3xl" />

                </div>

                <h3 className="font-bold text-gray-900">
                  Submitted Successfully!
                </h3>

                <p className="text-sm text-gray-500 mt-2">
                  The case has been submitted for
                  approval.
                </p>

                <button
                  onClick={closeSubmitModal}
                  className="mt-5 bg-teal-500 text-white px-5 py-2.5 rounded-xl"
                >
                  Done
                </button>

              </div>

            ) : (

              <div className="space-y-4">

                {/* CASE */}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Case
                  </label>

                  <input
                    value={submitCaseNumber}
                    readOnly
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50"
                  />

                </div>

                {/* TYPE */}


                {/* COMPLETION CONFIRMATIONS */}

                {submitType === 'case_closure' && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-teal-900">
                    Case Completion Checklist
                  </h4>

                  <label className="flex items-start gap-3 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={interventionCompleted}
                      onChange={(e) => setInterventionCompleted(e.target.checked)}
                      className="mt-1 accent-teal-600"
                    />
                    <span>
                      I confirm that the intervention plan and required case actions
                      have been completed and documented.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={childSafetyAssessed}
                      onChange={(e) => setChildSafetyAssessed(e.target.checked)}
                      className="mt-1 accent-teal-600"
                    />
                    <span>
                      I confirm that the child's current safety and protection needs
                      have been assessed and documented.
                    </span>
                  </label>
                </div>
              )}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Request Type
                  </label>

                  <select
                    value={submitType}
                    onChange={(e) =>
                      setSubmitType(
                        e.target.value
                      )
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                  >

                    <option value="intervention_plan">
                      Intervention Plan
                    </option>

                    <option value="financial_assistance">
                      Financial Assistance
                    </option>

                    <option value="case_closure">
                      Case Closure
                    </option>

                    <option value="escalation">
                      Escalation Request
                    </option>

                  </select>

                </div>

                {/* AMOUNT */}

                {submitType ===
                  'financial_assistance' && (

                  <div>

                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Amount Requested (₱)
                      <span className="text-red-500">
                        {' '}
                        *
                      </span>
                    </label>

                    <input
                      type="number"
                      min="0"
                      value={submitAmount}
                      onChange={(e) =>
                        setSubmitAmount(
                          e.target.value
                        )
                      }
                      placeholder="Enter amount..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                    />

                  </div>

                )}

                {/* DESCRIPTION */}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Description
                    <span className="text-red-500">
                      {' '}
                      *
                    </span>
                  </label>

                  <textarea
                    value={submitDesc}
                    onChange={(e) =>
                      setSubmitDesc(
                        e.target.value
                      )
                    }
                    rows={4}
                    maxLength={500}
                    placeholder="Describe the request..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none"
                  />

                </div>

                {/* BUTTONS */}

                <div className="flex gap-3 justify-end pt-2">

                  <button
                    onClick={
                      closeSubmitModal
                    }
                    className="text-sm border border-gray-200 px-4 py-2.5 rounded-xl hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={
                      handleSubmitApproval
                    }
                    disabled={
                      !submitDesc.trim() ||
                      (submitType === 'case_closure' &&
                        (!interventionCompleted || !childSafetyAssessed)) ||
                      (submitType === 'financial_assistance' &&
                        (!submitAmount || Number(submitAmount) <= 0))
                    }
                    className="text-sm bg-teal-500 text-white px-5 py-2.5 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <i className="ri-send-plane-line" />
                    Submit
                  </button>

                </div>

              </div>

            )}

          </div>

        </div>

      )}

    </AppLayout>
  );
}


function CaseStatusCard({
  title,
  value,
  icon,
  active = false,
}: {
  title: string;
  value: number;
  icon: string;
  active?: boolean;
}) {
  return (
    <div
      className={`
        rounded-2xl
        border
        p-4
        transition-all
        ${
          active
            ? 'bg-teal-500 border-teal-500 text-white'
            : 'bg-white border-gray-100 text-gray-900 shadow-sm'
        }
      `}
    >

      <div
        className={`
          w-10
          h-10
          rounded-xl
          flex
          items-center
          justify-center
          ${
            active
              ? 'bg-white/20 text-white'
              : 'bg-gray-100 text-gray-600'
          }
        `}
      >
        <i className={`${icon} text-xl`} />
      </div>

      <p
        className={`
          text-xs
          mt-3
          ${
            active
              ? 'text-white/80'
              : 'text-gray-500'
          }
        `}
      >
        {title}
      </p>

      <h2 className="text-2xl font-extrabold mt-1">
        {value}
      </h2>

    </div>
  );
}


function ReportStatusCard({
  title,
  value,
  icon,
  active = false,
}: {
  title: string;
  value: number;
  icon: string;
  active?: boolean;
}) {
  return (
    <div
      className={`
        rounded-2xl
        border
        p-4
        ${
          active
            ? 'bg-teal-500 border-teal-500 text-white'
            : 'bg-white border-gray-100 shadow-sm'
        }
      `}
    >

      <div
        className={`
          w-10
          h-10
          rounded-xl
          flex
          items-center
          justify-center
          ${
            active
              ? 'bg-white/20 text-white'
              : 'bg-gray-100 text-gray-600'
          }
        `}
      >
        <i className={`${icon} text-xl`} />
      </div>

      <p
        className={`
          text-xs mt-3
          ${
            active
              ? 'text-white/80'
              : 'text-gray-500'
          }
        `}
      >
        {title}
      </p>

      <h2 className="text-2xl font-extrabold mt-1">
        {value}
      </h2>

    </div>
  );
}


function CaseCard({
  caseItem,
  risk,
  statusTypes,
  onView,
  onUpdate,
  onSubmit,
}: {
  caseItem: CaseItem;
  risk: {
    score: number;
    level: string;
  };
  statusTypes: {
    name: string;
    code: string;
    description?: string;
    color?: string;
    active: boolean;
  }[];
  onView: () => void;
  onUpdate: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md hover:border-teal-200 transition-all">

      {/* RISK BAR */}

      <div
        className={`
          h-1.5 w-full
          ${
            risk.level === 'critical'
              ? 'bg-red-500'
              : risk.level === 'high'
              ? 'bg-orange-500'
              : risk.level === 'moderate'
              ? 'bg-amber-500'
              : 'bg-green-500'
          }
        `}
      />

      <div className="p-5">

        {/* NAME */}

        <div className="flex items-start justify-between gap-3">

          <div className="min-w-0">

            <h3 className="font-bold text-gray-900 truncate">
              {getChildName(caseItem)}
            </h3>

            <p className="text-xs text-gray-500 mt-1">
              {caseItem.age
                ? `${caseItem.age} yrs`
                : 'Age N/A'}

              {caseItem.gender
                ? ` · ${caseItem.gender}`
                : ''}

              {caseItem.incident_type
                ? ` · ${caseItem.incident_type}`
                : ''}
            </p>

            <p className="text-xs font-mono text-gray-400 mt-1">
              {caseItem.case_number}
            </p>

          </div>

          <span
            className={`
              text-xs
              px-2.5
              py-1
              rounded-full
              border
              font-semibold
              capitalize
              flex-shrink-0
              ${riskColor(risk.level)}
            `}
          >
            {risk.level} ({risk.score})
          </span>

        </div>

        {/* HIGH RISK */}

        {(risk.level === 'critical' ||
          risk.level === 'high') && (

          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 mt-4 flex items-center gap-2">

            <i className="ri-alarm-warning-line text-red-500" />

            <p className="text-xs text-red-700 font-medium">
              Requires immediate attention
            </p>

          </div>

        )}

        {/* STATUS */}

        <div className="flex items-center justify-between gap-3 mt-4">

          <span
            className={`
              text-xs
              px-3
              py-1.5
              rounded-full
              border
              font-semibold
              ${getCaseStatusStyle(
                caseItem.case_status,
                statusTypes
              )}
            `}
          >
            {formatStatus(
              caseItem.case_status
            )}
          </span>

          <span className="text-xs text-gray-400">
            {formatDate(
              caseItem.updated_at ||
                caseItem.created_at
            )}
          </span>

        </div>

        {/* PROGRESS NOTE */}

        {caseItem.progress_note && (

          <div className="mt-4 bg-gray-50 rounded-xl p-3">

            <p className="text-[11px] uppercase font-semibold text-gray-400">
              Latest Progress
            </p>

            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              {caseItem.progress_note}
            </p>

          </div>

        )}

        {/* ACTIONS */}

        <div className="grid grid-cols-3 gap-2 mt-4">

          <button
            onClick={onView}
            className="text-xs bg-gray-100 text-gray-700 py-2 rounded-xl hover:bg-gray-200 transition flex items-center justify-center gap-1"
          >
            <i className="ri-eye-line" />
            View
          </button>

          <button
            onClick={onUpdate}
            className="text-xs bg-teal-100 text-teal-700 py-2 rounded-xl hover:bg-teal-200 transition flex items-center justify-center gap-1"
          >
            <i className="ri-edit-line" />
            Update
          </button>

          <button
            onClick={onSubmit}
            className="text-xs bg-amber-100 text-amber-700 py-2 rounded-xl hover:bg-amber-200 transition flex items-center justify-center gap-1"
          >
            <i className="ri-send-plane-line" />
            Submit
          </button>

        </div>

      </div>

    </div>
  );
}


function CaseTable({
  cases,
  statusTypes,
  onView,
  onUpdate,
  onSubmit,
}: {
  statusTypes: {
    name: string;
    code: string;
    description?: string;
    color?: string;
    active: boolean;
  }[];
  cases: CaseItem[];
  onView: (caseNumber: string) => void;
  onUpdate: (caseNumber: string) => void;
  onSubmit: (caseNumber: string) => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      <div className="overflow-x-auto">

        <table className="w-full min-w-[1100px]">

          <thead className="bg-gray-50 border-b border-gray-100">

            <tr className="text-left text-xs font-semibold text-gray-500 uppercase">

              <th className="px-5 py-4">
                Case Number
              </th>

              <th className="px-5 py-4">
                Child
              </th>

              <th className="px-5 py-4">
                Incident
              </th>

              <th className="px-5 py-4">
                Risk
              </th>

              <th className="px-5 py-4">
                Status
              </th>

              <th className="px-5 py-4">
                Progress
              </th>

              <th className="px-5 py-4 text-right">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {cases.map((c) => {

              const risk = getRisk(c);

              return (

                <tr
                  key={c.case_number}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >

                  {/* CASE */}

                  <td className="px-5 py-4">

                    <p className="text-sm font-mono font-semibold text-gray-800">
                      {c.case_number}
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(
                        c.created_at
                      )}
                    </p>

                  </td>

                  {/* CHILD */}

                  <td className="px-5 py-4">

                    <p className="text-sm font-semibold text-gray-800">
                      {getChildName(c)}
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      {c.age
                        ? `${c.age} years old`
                        : 'Age N/A'}

                      {c.gender
                        ? ` · ${c.gender}`
                        : ''}
                    </p>

                  </td>

                  {/* INCIDENT */}

                  <td className="px-5 py-4">

                    <p className="text-sm text-gray-700">
                      {c.incident_type ||
                        'Not specified'}
                    </p>

                  </td>

                  {/* RISK */}

                  <td className="px-5 py-4">

                    <span
                      className={`
                        px-2.5
                        py-1
                        rounded-full
                        border
                        text-xs
                        font-semibold
                        capitalize
                        ${riskColor(
                          risk.level
                        )}
                      `}
                    >
                      {risk.level}
                      {' '}
                      ({risk.score})
                    </span>

                  </td>

                  {/* STATUS */}

                  <td className="px-5 py-4">

                    <span
                      className={`
                        px-2.5
                        py-1
                        rounded-full
                        border
                        text-xs
                        font-semibold
                        getCaseStatusStyle(
                          c.case_status,
                          statusTypes
                        )
                      `}
                    >
                      {formatStatus(
                        c.case_status
                      )}
                    </span>

                  </td>

                  {/* PROGRESS */}

                  <td className="px-5 py-4 max-w-[260px]">

                    <p className="text-xs text-gray-500 truncate">
                      {c.progress_note ||
                        'No progress note'}
                    </p>

                  </td>

                  {/* ACTIONS */}

                  <td className="px-5 py-4">

                    <div className="flex justify-end gap-2">

                      <button
                        onClick={() =>
                          onView(
                            c.case_number
                          )
                        }
                        className="w-9 h-9 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200"
                        title="View"
                      >
                        <i className="ri-eye-line" />
                      </button>

                      <button
                        onClick={() =>
                          onUpdate(
                            c.case_number
                          )
                        }
                        className="w-9 h-9 rounded-xl bg-teal-100 text-teal-700 hover:bg-teal-200"
                        title="Update"
                      >
                        <i className="ri-edit-line" />
                      </button>

                      <button
                        onClick={() =>
                          onSubmit(
                            c.case_number
                          )
                        }
                        className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200"
                        title="Submit"
                      >
                        <i className="ri-send-plane-line" />
                      </button>

                    </div>

                  </td>

                </tr>

              );
            })}

          </tbody>

        </table>

      </div>

    </div>
  );
}

function LoadingState({
  text,
}: {
  text: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">

      <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto">

        <i className="ri-loader-4-line animate-spin text-teal-500 text-2xl" />

      </div>

      <p className="text-sm text-gray-500 mt-4">
        {text}
      </p>

    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">

      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">

        <i
          className={`${icon} text-gray-400 text-3xl`}
        />

      </div>

      <h3 className="font-bold text-gray-800 mt-4">
        {title}
      </h3>

      <p className="text-sm text-gray-400 mt-1">
        {description}
      </p>

    </div>
  );
}

function AssessmentStatCard({
  icon,
  title,
  value,
  color,
}: {
  icon: string;
  title: string;
  value: number;
  color: 'blue' | 'red' | 'orange' | 'green';
}) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    red: 'bg-red-100 text-red-600',
    orange: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

      <div
        className={`
          w-12
          h-12
          rounded-2xl
          flex
          items-center
          justify-center
          ${colors[color]}
        `}
      >

        <i
          className={`${icon} text-xl`}
        />

      </div>

      <p className="text-sm text-gray-500 mt-4">
        {title}
      </p>

      <h2 className="text-3xl font-bold text-gray-900 mt-1">
        {value}
      </h2>

    </div>
  );
}


function AssessmentDetailCard({
  title,
  icon,
  content,
}: {
  title: string;
  icon: string;
  content: string;
}) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

      <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">

        <i
          className={`${icon} text-blue-600`}
        />

        {title}

      </h3>

      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">

        {content}

      </p>

    </div>
  );
}

function ReferralsSection({
  cases,
  onViewDetails,
  onReassign,
  onMarkPriority,
}: {
  cases: any[];
  onViewDetails: (caseItem: any) => void;
  onReassign: (caseNumber: string) => void;
  onMarkPriority: (caseItem: any) => void;
}) {
  return (
    <div className="space-y-4">

      {/* HEADER */}

      <div className="flex items-center justify-between">

        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Referrals
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Cases referred to social workers for
            assessment and intervention.
          </p>
        </div>

        <div className="
          px-4
          py-2
          rounded-xl
          bg-teal-50
          text-teal-700
          text-sm
          font-semibold
        ">
          {cases.length} Cases
        </div>

      </div>

      {/* CASE LIST */}

      {cases.length === 0 ? (

        <div className="
          bg-white
          rounded-3xl
          border
          border-gray-100
          shadow-sm
          p-12
          text-center
        ">

          <div className="
            w-16
            h-16
            rounded-2xl
            bg-gray-100
            flex
            items-center
            justify-center
            mx-auto
            mb-4
          ">

            <i className="
              ri-user-shared-line
              text-2xl
              text-gray-400
            " />

          </div>

          <h3 className="font-bold text-gray-800">
            No Referrals
          </h3>

          <p className="text-sm text-gray-400 mt-1">
            There are currently no referred cases.
          </p>

        </div>

      ) : (

        <div className="space-y-4">

          {cases.map((caseItem) => (

            <ReferralCaseCard
              key={
                caseItem.case_number ||
                caseItem.id
              }
              caseItem={caseItem}
              onViewDetails={onViewDetails}
              onReassign={onReassign}
              onMarkPriority={onMarkPriority}
            />

          ))}

        </div>

      )}

    </div>
  );
}



function ReferralCaseCard({
  caseItem,
  onViewDetails,
  onReassign,
  onMarkPriority,
}: {
  caseItem: any;
  onViewDetails: (caseItem: any) => void;
  onReassign: (caseNumber: string) => void;
  onMarkPriority: (caseItem: any) => void;
}) {

  const fullName = [
    caseItem.first_name,
    caseItem.middle_name,
    caseItem.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Unknown Child';

  const riskLevel =
    caseItem.risk_level ||
    caseItem.risk ||
    'Low';

  const riskScore =
    caseItem.risk_score ??
    caseItem.score ??
    0;

  const incident =
    caseItem.incident_type ||
    'Not specified';

  const location =
    caseItem.barangay ||
    caseItem.location ||
    caseItem.incident_location ||
    'Not specified';

  const assignedTo =
    caseItem.assigned_to ||
    caseItem.assigned_name ||
    caseItem.social_worker_name ||
    'Unassigned';

  const createdAt =
    caseItem.created_at ||
    caseItem.report_date ||
    '';

  const isCritical =
    String(riskLevel)
      .toLowerCase()
      .includes('critical');

  const isHigh =
    String(riskLevel)
      .toLowerCase()
      .includes('high');

  const riskBadgeClass = isCritical
    ? 'bg-red-100 text-red-600'
    : isHigh
      ? 'bg-orange-100 text-orange-600'
      : 'bg-yellow-100 text-yellow-700';

  const cardClass = isCritical
    ? 'border-red-200 bg-red-50/30'
    : isHigh
      ? 'border-orange-200 bg-orange-50/20'
      : 'border-yellow-200 bg-yellow-50/20';

  return (
    <div
      className={`
        bg-white
        rounded-2xl
        border
        overflow-hidden
        shadow-sm
        ${cardClass}
      `}
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="
        px-6
        py-5
        border-b
        border-gray-100
        flex
        items-center
        justify-between
        gap-4
      ">

        <div className="flex items-center gap-3">

          <div className="
            w-2
            h-2
            rounded-full
            bg-red-500
            flex-shrink-0
          " />

          <div>

            <h3 className="
              font-semibold
              text-gray-900
              text-base
            ">
              {fullName}
            </h3>

            <p className="
              text-xs
              text-gray-500
              mt-0.5
            ">
              {caseItem.case_number ||
                'No Case Number'}
            </p>

          </div>

        </div>

      </div>

      {/* =====================================================
          INFORMATION
      ===================================================== */}

      <div className="
        px-6
        py-5
        grid
        grid-cols-1
        md:grid-cols-2
        xl:grid-cols-4
        gap-5
      ">

        {/* INCIDENT */}

        <div>

          <p className="
            text-xs
            text-gray-400
            mb-1
          ">
            Incident
          </p>

          <p className="
            text-sm
            font-semibold
            text-gray-900
          ">
            {incident}
          </p>

        </div>

        {/* LOCATION */}

        <div>

          <p className="
            text-xs
            text-gray-400
            mb-1
          ">
            Location
          </p>

          <p className="
            text-sm
            font-semibold
            text-gray-900
          ">
            {location}
          </p>

        </div>

        {/* ASSIGNED TO */}

        <div>

          <p className="
            text-xs
            text-gray-400
            mb-1
          ">
            Assigned To
          </p>

          <p
            className={`
              text-sm
              font-semibold
              ${
                assignedTo === 'Unassigned'
                  ? 'text-gray-500'
                  : 'text-gray-900'
              }
            `}
          >
            {assignedTo}
          </p>

        </div>

        {/* CREATED */}

        <div>

          <p className="
            text-xs
            text-gray-400
            mb-1
          ">
            Created At
          </p>

          <p className="
            text-sm
            font-semibold
            text-gray-900
          ">
            {formatReferralDate(createdAt)}
          </p>

        </div>

      </div>

      {/* =====================================================
          ACTIONS
      ===================================================== */}

      <div className="
        px-6
        pb-5
        flex
        flex-wrap
        gap-2
      ">

        {/* VIEW */}

        <button
          type="button"
          onClick={() =>
            onViewDetails(caseItem)
          }
          className="
            px-4
            py-2.5
            rounded-xl
            border
            border-gray-200
            bg-white
            text-sm
            font-medium
            text-gray-700
            hover:bg-gray-50
            transition
            flex
            items-center
            gap-2
          "
        >
          <i className="ri-eye-line" />

          View Details
        </button>

        {/* REASSIGN */}

        <button
          type="button"
          onClick={() =>
            onReassign(
              caseItem.case_number
            )
          }
          className="
            px-4
            py-2.5
            rounded-xl
            bg-amber-500
            text-white
            text-sm
            font-semibold
            hover:bg-amber-600
            transition
            flex
            items-center
            gap-2
          "
        >
          <i className="ri-user-shared-line" />

          Reassign
        </button>

        {/* PRIORITY */}

        <button
          type="button"
          onClick={() =>
            onMarkPriority(caseItem)
          }
          className="
            px-4
            py-2.5
            rounded-xl
            bg-red-500
            text-white
            text-sm
            font-semibold
            hover:bg-red-600
            transition
            flex
            items-center
            gap-2
          "
        >
          <i className="ri-alarm-warning-line" />

          Mark Priority
        </button>

      </div>

    </div>
  );
}

function formatReferralDate(
  value: string
) {
  if (!value) {
    return '—';
  }

  const date = new Date(
    value.replace(' ', 'T')
  );

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    'en-US',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }
  );
}